import { useEffect, useRef, useState } from "react";

const GAP = 16; // space between the dot and the cursor pointer tip
const SIDE_OFFSET = 8; // shift the dot slightly to the left of the cursor

const DISABLE_SELECTORS =
  'a, button, [role="button"], input, textarea, select, label, summary, nav, header, [data-cursor="disable"]';

const CustomCursor = () => {
  const dotRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [visible, setVisible] = useState(false);
  const [productLabel, setProductLabel] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const hoveringRef = useRef(false);
  const offsetRef = useRef(GAP + 10 / 2); // keep dot just above the pointer tip

  useEffect(() => {
    // Disable on touchscreens / mobile devices and when reduced motion is preferred
    const isTouch =
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(pointer: coarse)").matches ||
      ("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouch || reducedMotion) return;
    setEnabled(true);

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const pos = { x: target.x, y: target.y };
    let raf = 0;

    const render = () => {
      pos.x += (target.x - pos.x) * 0.2;
      pos.y += (target.y - pos.y) * 0.2;

      // Smoothly update the vertical gap so the dot stays above the pointer
      // even as it expands on hover.
      const targetSize = hoveringRef.current ? 36 : 10;
      const targetOffset = targetSize / 2 + GAP;
      offsetRef.current += (targetOffset - offsetRef.current) * 0.2;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${pos.x - SIDE_OFFSET}px, ${pos.y - offsetRef.current}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    const onMove = (e: MouseEvent) => {
      target.x = e.clientX;
      target.y = e.clientY;
      if (!visible) setVisible(true);
      const el = e.target as HTMLElement | null;

      // Disable the custom cursor on important interactive surfaces (links, buttons, nav, etc.)
      // so the native cursor remains clear and usable.
      const important = !!el?.closest(DISABLE_SELECTORS);
      if (important) {
        setDisabled(true);
        setProductLabel(null);
        hoveringRef.current = false;
        setHovering(false);
        return;
      }

      const productSection = el?.closest<HTMLElement>("[data-cursor='product']");
      if (productSection) {
        const label = productSection.dataset.cursorLabel || null;
        setProductLabel(label);
        setDisabled(false);
        hoveringRef.current = true;
        setHovering(true);
        return;
      }

      const interactive = !!el?.closest('[data-cursor="interactive"]');
      setProductLabel(null);
      setDisabled(false);
      hoveringRef.current = interactive;
      setHovering(interactive);
    };
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  if (!enabled) return null;

  const isProductHover = Boolean(productLabel) && !disabled;

  return (
    <div
      ref={dotRef}
      aria-hidden="true"
      className="pointer-events-none fixed left-0 top-0 z-[9999] flex items-center justify-center rounded-full transition-[width,height,opacity,background-color] duration-200 ease-out"
      style={{
        width: isProductHover ? "auto" : hovering ? 36 : 10,
        height: isProductHover ? 36 : hovering ? 36 : 10,
        minWidth: isProductHover ? 80 : undefined,
        padding: isProductHover ? "0 16px" : undefined,
        opacity: disabled ? 0 : visible ? (hovering ? 0.9 : 0.9) : 0,
        mixBlendMode: isProductHover ? undefined : "multiply",
        backgroundColor: isProductHover ? "rgba(250, 249, 246, 0.95)" : undefined,
        color: isProductHover ? "#1F2A24" : undefined,
        boxShadow: isProductHover ? "0 4px 24px rgba(0, 0, 0, 0.12)" : undefined,
      }}
    >
      {isProductHover && (
        <span className="text-sm font-medium tracking-wide whitespace-nowrap">{productLabel}</span>
      )}
      {!isProductHover && !disabled && (
        <div
          className="rounded-full bg-forest-moss"
          style={{ width: hovering ? 36 : 10, height: hovering ? 36 : 10 }}
        />
      )}
    </div>
  );
};

export default CustomCursor;
